import { PerpsProvider } from '@kwenta/sdk/types'
import Badge from 'components/Badge'
import Pill from 'components/Pill'
import { FlexDivCol, FlexDivRow, FlexDivRowCentered } from 'components/layout/flex'
import { PROVIDER_MAINNET_CHAINS, providerChainIcon } from 'constants/network'
import Connector from 'containers/Connector'
import { useMarketPageRoute } from 'hooks/useMarketPageRoute'
import useWindowSize from 'hooks/useWindowSize'
import { isEqual } from 'lodash'
import { useRouter } from 'next/router'
import type React from 'react'
import { type FC, memo, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { selectAllNetworks, selectPerpsProvider } from 'state/futures/common/selectors'
import { selectIsolatedPositionsCount } from 'state/futures/isolatedMargin/selectors'
import { setPreferences } from 'state/futures/reducer'
import { selectSubAccountsCounts } from 'state/futures/selectors'
import { selectActiveCrossMarginPositionsCount } from 'state/futures/snxPerpsV3/selectors'
import { useAppSelector } from 'state/hooks'
import styled, { css } from 'styled-components'

type PerpsProviderSwitcherProps = {
	onlyIcons?: boolean
	showAllNetworks?: boolean
	showCount?: 'positions' | 'subAccounts'
}

type SelectorProps = {
	label: string
	options: React.ReactNode[]
	align?: 'left' | 'right'
}

type ProviderSelection = PerpsProvider | 'All Networks'

type ProviderConfigItem = {
	key: ProviderSelection
	label: string
	positionsCount: number
	subAccountsCount: number
}

const Selector: FC<SelectorProps> = memo(({ options, align = 'left' }) => {
	return (
		<SelectorContainer $align={align}>
			<PillsContainer>{...options}</PillsContainer>
		</SelectorContainer>
	)
})

const PerpsProviderSwitcher = memo(
	({
		onlyIcons = false,
		showAllNetworks = false,
		showCount = 'positions',
	}: PerpsProviderSwitcherProps) => {
		const { t } = useTranslation()
		const router = useRouter()
		const dispatch = useDispatch()
		const { switchToChain } = Connector.useContainer()
		const selectedProvider = useAppSelector(selectPerpsProvider)
		const isAllNetworksSelected = useAppSelector(selectAllNetworks)
		const isolatedPositionsCount = useAppSelector(selectIsolatedPositionsCount, isEqual)
		const crossPositionsCount = useAppSelector(selectActiveCrossMarginPositionsCount, isEqual)
		const subAccountsCount = useAppSelector(selectSubAccountsCounts, isEqual)
		const { deviceType } = useWindowSize()
		const isMobile = useMemo(() => deviceType === 'mobile', [deviceType])
		const isMarketsPage = useMemo(() => router.pathname.startsWith('/market'), [router.pathname])
		const marketPageRoute = useMarketPageRoute()

		const handleSelect = useCallback(
			(selection: ProviderSelection) => {
				if (selection === 'All Networks') {
					dispatch(setPreferences({ selectedAllNetworks: true }))
				} else {
					dispatch(
						setPreferences({
							selectedProvider: selection as PerpsProvider,
							selectedAllNetworks: false,
						})
					)
					if (isMarketsPage) {
						router.push(marketPageRoute(selection as PerpsProvider))
						switchToChain(PROVIDER_MAINNET_CHAINS[selection as PerpsProvider])
					}
				}
			},
			[dispatch, isMarketsPage, marketPageRoute, switchToChain, router]
		)

		const providersConfig = useMemo(() => {
			const configs: ProviderConfigItem[] = [
				{
					key: PerpsProvider.SNX_V3_BASE,
					label: t('header.nav.perps-provider-switcher.base'),
					positionsCount: crossPositionsCount[PerpsProvider.SNX_V3_BASE],
					subAccountsCount: subAccountsCount[PerpsProvider.SNX_V3_BASE],
				},
				{
					key: PerpsProvider.SNX_V2_OP,
					label: t('header.nav.perps-provider-switcher.optimism'),
					positionsCount: isolatedPositionsCount[PerpsProvider.SNX_V2_OP],
					subAccountsCount: subAccountsCount[PerpsProvider.SNX_V2_OP],
				},
			]

			if (showAllNetworks) {
				configs.unshift({
					key: 'All Networks',
					label: t(`header.nav.perps-provider-switcher.all-networks${isMobile ? '-mobile' : ''}`),
					positionsCount:
						Object.values(crossPositionsCount).reduce((a, b) => a + b, 0) +
						Object.values(isolatedPositionsCount).reduce((a, b) => a + b, 0),
					subAccountsCount: Object.values(subAccountsCount).reduce((a, b) => a + b, 0),
				})
			}

			return configs
		}, [
			t,
			crossPositionsCount,
			isolatedPositionsCount,
			subAccountsCount,
			showAllNetworks,
			isMobile,
		])

		const isPillSelected = useCallback(
			(selection: ProviderSelection) => {
				if (showAllNetworks) {
					if (selection === 'All Networks' && isAllNetworksSelected) {
						return true
					}
					return selectedProvider === selection && !isAllNetworksSelected
				}
				return selectedProvider === selection
			},
			[selectedProvider, isAllNetworksSelected, showAllNetworks]
		)

		const options = useMemo(
			() =>
				providersConfig.map(({ key, label, positionsCount, subAccountsCount }) => (
					<StyledPill
						isMobile={isMobile}
						key={key}
						color="gray"
						weight="bold"
						$selected={isPillSelected(key as ProviderSelection)}
						onClick={() => handleSelect(key as ProviderSelection)}
					>
						<PillLabel $onlyIcons={(onlyIcons || isMobile) && key !== 'All Networks'}>
							{label}
						</PillLabel>
						{key !== 'All Networks' &&
							providerChainIcon(key as PerpsProvider, { width: 17, height: 17 })}
						<BadgeContainer columnGap="3px">
							{showCount === 'positions' ? (
								positionsCount ? (
									<StyledBadge size="small" font="semiBold">
										{positionsCount}
									</StyledBadge>
								) : null
							) : showCount === 'subAccounts' ? (
								subAccountsCount ? (
									<StyledBadge size="small" font="semiBold">
										{subAccountsCount}
									</StyledBadge>
								) : null
							) : null}
						</BadgeContainer>
					</StyledPill>
				)),
			[isPillSelected, handleSelect, onlyIcons, providersConfig, showCount, isMobile]
		)
		return <Selector options={options} label="Provider" align="right" />
	}
)

export default PerpsProviderSwitcher

const SelectorContainer = styled(FlexDivCol)<{ $align: 'left' | 'right' }>`
	position: relative;
	gap: 10px;
	${(props) =>
		props.$align === 'right' &&
		css`
		align-items: flex-end;
	`}
`

const PillsContainer = styled(FlexDivRow)`
	gap: 8px;
`

const StyledPill = styled(Pill)<{ $selected: boolean; isMobile: boolean }>`
	position: relative;
	text-transform: none;
	height: 27px;
	padding: 3px ${({ isMobile }) => (isMobile ? '6px' : '10px')};
	display: flex;
	align-items: center;
	gap: 7px;
	justify-content: center;
	font-size: 12px;
	border: 1px solid transparent;
	transition: all 0.25s ease-in-out;

	svg {
		transition: filter 0.25s ease-in-out;
	}

	${(props) =>
		props.$selected
			? css`
					color: ${props.theme.colors.selectedTheme.newTheme.text.primary};
					background: ${props.theme.colors.selectedTheme.newTheme.pill.gray.hover.background};
				`
			: css`
					svg {
						filter: grayscale(100%) brightness(70%);
					}
					background: transparent;
				`}

	${(props) => `
	&:hover {
		color: ${props.theme.colors.selectedTheme.newTheme.text.primary};
		svg {
			filter: none;
		}
	}
	`}
`

const PillLabel = styled.span<{ $onlyIcons: boolean }>`
	text-transform: capitalize;
	${(props) =>
		props.$onlyIcons &&
		css`
			display: none;
		`}
`

const BadgeContainer = styled(FlexDivRowCentered)`
	position: absolute;
	bottom: 22px;
	right: -1px;
	justify-content: flex-end;
`

const StyledBadge = styled(Badge)`
	padding: 2px 6px;
	border: 1px solid
		${(props) => props.theme.colors.selectedTheme.newTheme.button.switcher.active.border};
`
